import { useEffect, useMemo, useRef, useState } from "react";
import { dev_console, isAbleAccesser, whoAmI } from "../../Common/ts/commonTools";
import ThemeBook from "./ThemeBook";
import TitleBtnWrap from "../../Component/Elements/TitleBtnWrap/TitleBtnWrap";
import CSS from "./static/css/themeBookHome.module.css";
import useOfficeDataREST from "./hooks/useOfficeDataREST.hook";
import usePhotoProductDataREST from "./hooks/usePhotoProductDataREST.hook";
import useThemaBookDetailListREST from "./hooks/useThemaBookDetailListREST.hook";
import { photoProductTypeCodeType } from "./static/interface/themeBook.interface";
import { REGION_LIST } from "../../Common/ts/const";
import { PHOTO_PRODUCT_TYPE_CODE } from "./static/ts/const";
import { useSearchParams } from "react-router-dom";
import { themeBookParamsData } from "./static/ts/ThemeBook.tools";
import useStaffOfficeListREST from "./hooks/useStaffOfficeListREST.hook";
import CardContainer from "../../Component/Elements/CardContainer/CardContainer";

const ThemeBookHome = () => {
    const l_is_admin = whoAmI.isAllEmployee();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [params, setParams] = useSearchParams();

    const {
        l_region_id,
        l_region_value,
        l_office_id,
        l_photo_product_id,
    } = themeBookParamsData({URLSearchParams: params})

    // 선택된 필터정보
    const [selectedRegionId, setSelectedRegionId] = useState<number>(l_region_id);
    const [selectedOfficeId, setSelectedOfficeId] = useState<number>(l_office_id);
    const [selectedPhotoProductId, setSelectedPhotoProductId] = useState<number>(l_photo_product_id);

    // 선택한 지역목록에 따른 지점 목록 요청
    const {
        officeList,
        isOfficeListLoading,
    } = useOfficeDataREST({
        region: l_region_value
    })

    // 관리자 페이지 에서 출력되는 지점 목록
    const {
        staffoOfficeList,
        isStaffoOfficeListLoading,
    } = useStaffOfficeListREST();

    // 선택된 지점에 따른 촬영 상품 목록 요청
    const {
        photoProductList,
        photoProductError,
        isPhotoProductLoading,
    } = usePhotoProductDataREST({
        is_has_office_list: hasOfficeList(),
        office_id: selectedOfficeId
    })

    const l_is_has_photo_product_list = photoProductList.length > 0;
    const l_is_available_office_id = officeList.some((item) => item.office_id === l_office_id) || l_is_admin; //관리자 계정인 경우는 통과시킨다.

    // 최종적으로 테마 목록 값 요청
    const {
        themeList,
        themeListError,
        isThemeListLoading,
    } = useThemaBookDetailListREST({
        is_available_office_id: l_is_available_office_id,
        is_has_photo_product_list: l_is_has_photo_product_list,
        office_id: selectedOfficeId,
        photo_product_id: selectedPhotoProductId,
        photo_product_type: selectedPhotoProductType(),
    })

    // 지점 목록이 존재하는가?
    function hasOfficeList(){
        let RTN: boolean = false;

        if(whoAmI.isAllEmployee()){
            RTN = staffoOfficeList.length > 0 && staffoOfficeList.some((item) => item.office_id === selectedOfficeId)
        }else if(whoAmI.isCustomer()){
            RTN = officeList.length > 0 && officeList.some((item) => item.office_id === selectedOfficeId)
        }

        return RTN;
    }

    // 상품의 타입을 설정
    function selectedPhotoProductType(){
        let RTN: photoProductTypeCodeType = PHOTO_PRODUCT_TYPE_CODE.none;

        PhotoProductSetting:{
            if(photoProductList.length === 0){
                break PhotoProductSetting;
            }

            // selectedPhotoProductId가 0이라는 뜻은, 전체 데이터를 모두 호출하겠다는 뜻이다.
            if(selectedPhotoProductId === 0){
                RTN = PHOTO_PRODUCT_TYPE_CODE.all
                break PhotoProductSetting;
            }

            const m_selected_photo_product = photoProductList.find((item) => item.photo_id === selectedPhotoProductId);
            if(m_selected_photo_product){
                RTN = m_selected_photo_product.type;
            }
        }

        return RTN;
    };

    // 쿼리스트링 파라미터 설정
    function paramsSetter() {
        const newParams = new URLSearchParams();

        newParams.set("tab1", selectedRegionId.toString());
        newParams.set("tab2", selectedOfficeId.toString());
        newParams.set("tab3", selectedPhotoProductId.toString());

        setParams(newParams, {replace: true});
    }

    useEffect(() => {
        // 접속이 가능한 계정인지 검증
        isAbleAccesser({
            allowedUserTypeList: ["C", "E", "O"],
        });
    }, [])

    // URLSearchParams가 바뀌면 한번에 업데이트 시킨다.
    useEffect(() => {
        const {
            l_region_id,
            l_office_id,
            l_photo_product_id,
        } = themeBookParamsData({URLSearchParams: params})

        // 지역 id설정
        setSelectedRegionId(l_region_id);
        // 지점 id설정
        setSelectedOfficeId(l_office_id)
        // 촬영상품 id설정
        setSelectedPhotoProductId(l_photo_product_id)
    }, [URLSearchParams])

    // 지점 목록에 변화가 발생한 후(지역을 새로 선택한 후) 지점 목록에 해당 지점이 존재하는지 확인하고 없으면 첫번째 지점으로 설정
    useEffect(() => {
        OfficeIdSetting:{
            if(officeList.length === 0){
                break OfficeIdSetting;
            }

            const m_office = officeList.find((item) => item.office_id === selectedOfficeId);
            if(!m_office){
                setSelectedOfficeId(officeList[0].office_id);
            }else{
                setSelectedOfficeId(m_office.office_id);
            }
        }
    }, [officeList, selectedOfficeId])

    // 촬영 상품 목록에 변화가 발생한 후(지점을 새로 선택한 후) 촬영 상품 목록에 해당 촬영 상품이 존재하는지 확인하고 없으면 첫번째 촬영 상품으로 설정
    useEffect(() => {
        PhotoProductIdSetting:{
            if(photoProductList.length === 0){
                break PhotoProductIdSetting;
            }

            const m_photo_product = photoProductList.find((item) => item.photo_id === selectedPhotoProductId);
            if(!m_photo_product){
                setSelectedPhotoProductId(photoProductList[0].photo_id);
            }else{
                setSelectedPhotoProductId(m_photo_product.photo_id);
            }
        }
    }, [photoProductList, selectedPhotoProductId])

    // 통합 로딩 처리
    useEffect(() => {
        setIsLoading(isOfficeListLoading || isPhotoProductLoading || isThemeListLoading || isStaffoOfficeListLoading)
    }, [isOfficeListLoading, isPhotoProductLoading, isThemeListLoading, isStaffoOfficeListLoading])

    // 선택한 텝 메뉴 내용을 쿼리파라미터에 반영해준다.
    useEffect(() => {
        paramsSetter();
    }, [selectedRegionId, selectedOfficeId, selectedPhotoProductId])

    if(l_is_admin){
        return (
            <ThemeBook
                themeList={themeList || []}
                isLoading={isLoading}
            >
                <div className={CSS.l_ThemeBookHome_filterContainer}>
                    <TitleBtnWrap<number>
                        title={"지점 선택"}
                        valueState={selectedOfficeId}
                        setValueState={setSelectedOfficeId}
                        value_list={staffoOfficeList.map((item) => item.office_id)}
                        innerText_list={staffoOfficeList.map((item) => `${item.office_name}`)}
                        is_activated={true}
                        is_print_badge_list={[]}
                        is_loading={isLoading}
                        category_tag="ThemeBook_selectedOfficeId"
                    />

                    <TitleBtnWrap<number>
                        title={"테마 선택"}
                        valueState={selectedPhotoProductId}
                        setValueState={setSelectedPhotoProductId}
                        value_list={photoProductList.map((item) => item.photo_id)}
                        innerText_list={photoProductList.map((item) => item.photo_name)}
                        is_activated={true}
                        is_print_badge_list={[]}
                        is_loading={isLoading}
                        category_tag="ThemeBook_selectedPhotoProduct"
                    />
                </div>
            </ThemeBook>
        )
    }else{
        return (
            <ThemeBook
                themeList={themeList || []}
                isLoading={isLoading}
            >
                <div className={CSS.l_ThemeBookHome_filterContainer}>
                    <TitleBtnWrap<number>
                        title={"지역 선택"}
                        valueState={selectedRegionId}
                        setValueState={setSelectedRegionId}
                        value_list={REGION_LIST.map((item) => item.id)}
                        innerText_list={REGION_LIST.map((item) => item.name)}
                        is_activated={true}
                        is_print_badge_list={[]}
                        is_loading={isLoading}
                        category_tag="ThemeBook_selectedRegion"
                    />

                    <TitleBtnWrap<number>
                        title={"지점 선택"}
                        valueState={selectedOfficeId}
                        setValueState={setSelectedOfficeId}
                        value_list={!!officeList ? officeList.map((item) => item.office_id) : []}
                        innerText_list={!!officeList ? officeList.map((item) => `${item.office_name}`) : []}
                        is_activated={true}
                        is_print_badge_list={[]}
                        is_loading={isLoading}
                        category_tag="ThemeBook_selectedOfficeId"
                    />

                    <TitleBtnWrap<number>
                        title={"테마 선택"}
                        valueState={selectedPhotoProductId}
                        setValueState={setSelectedPhotoProductId}
                        value_list={photoProductList.map((item) => item.photo_id)}
                        innerText_list={photoProductList.map((item) => item.photo_name)}
                        is_activated={true}
                        is_print_badge_list={[]}
                        is_loading={isLoading}
                        category_tag="ThemeBook_selectedPhotoProduct"
                    />
                </div>
            </ThemeBook>
        )
    }
}

export default ThemeBookHome;